import { Button } from "@/components/ui/button";
import { ORDER_TYPES, PRODUCT_TYPES_FUTURES, PRODUCT_TYPES_STOCKS } from "@/lib/placeOrder";
import { getInstrument, inRangeOrNot, isFuturesOrOptions, isOptions } from "@/lib/tradeCards";
import { cn, capitalized } from "@/lib/utils";
import {
    resetPlaceOrder,
    setOpenPlaceOrderBasketSlice,
    setOpenPlaceOrderSlice,
    updateMultiField
} from "@/store/features/placeOrderSlice";
import React from "react";
import { useDispatch } from "react-redux";
import Image from "next/image";
import Hint from "../Hint";
import { Mixpanel } from "@/elements/Mixpanel";
import { FaArrowRight } from "react-icons/fa";

export default function BuySellBtn({
    disabled = false,
    type = "",
    finCode,
    exchange,
    callBack = () => {},
    symbol,
    pageName
}) {
    const dispatch = useDispatch();

    return (
        <Hint label={capitalized(type)} side="top">
            <Button
                disabled={disabled}
                id={`buy-sell-btn-${symbol}`}
                variant="outline"
                className={cn("p-0 border-none w-7 h-7 text-xs font-bold text-white hover:text-white", {
                    "bg-[#26A649] hover:bg-[#26A649BF]": type === "BUY",
                    "bg-[#EB4E2C] hover:bg-[#EB4E2CBF]": type === "SELL"
                })}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    callBack();
                    dispatch(resetPlaceOrder());
                    dispatch(
                        setOpenPlaceOrderSlice({
                            isOpen: true,
                            type,
                            finCode,
                            exchange,
                            source: pageName
                        })
                    );
                    Mixpanel.track("cta_clicked", {
                        page: pageName,
                        cta_text: type,
                        symbol: symbol,
                        expiry_date: null,
                        instrument_name: getInstrument(symbol),
                        is_watchlisted: null,
                        source: pageName
                    });
                }}
            >
                {type?.slice(0, 1)}
            </Button>
        </Hint>
    );
}

export function TradeCardBuyBtn({
    type: tempType,
    finCode,
    tradeCardData,
    source,
    tab,
    ele,
    hitOrMiss,
    isMiniTradeCard = false,
    classNames = "",
    isClosed = false,
    disabled = false,
    onClick = () => {}
}) {
    const type = tempType === "BUY" || tempType === "OPTIONS" ? "BUY" : "SELL";
    const dispatch = useDispatch();
    const exchange =
        ["SENSEX", "BANKEX"]?.filter((ele) =>
            `${tradeCardData?.nseSymbol}-${tradeCardData?.bseSymbol}-${tradeCardData?.compName}-${tradeCardData?.stockName}-${tradeCardData?.name}`?.includes(
                ele
            )
        )?.length > 0
            ? "BSE"
            : "NSE";

    return (
        <Button
            id="buy-btn"
            disabled={isClosed || disabled}
            variant={isMiniTradeCard ? "outline" : "default"}
            className={cn(
                "p-0 w-full h-9 text-base font-bold flex gap-2 ",
                {
                    "text-primary/80 hover:text-primary border-[1.5px] border-primary/80": isMiniTradeCard,
                    "bg-primary/80 disabled:opacity-65": !isMiniTradeCard
                },
                classNames
            )}
            onClick={(e) => {
                onClick();
                e.preventDefault();
                e.stopPropagation();
                dispatch(resetPlaceOrder());
                if (isOptions(tradeCardData.term)) {
                    const tradeCardOptionsDataList = tradeCardData.tradeCardOptionsDataList;
                    if (tradeCardOptionsDataList.length === 1) {
                        dispatch(
                            setOpenPlaceOrderSlice({
                                exchange,
                                isOpen: true,
                                type: tradeCardOptionsDataList[0].recommendationType,
                                finCode: tradeCardOptionsDataList[0].finCode,
                                tradeCardData,
                                source: source
                            })
                        )
                            .unwrap()
                            .then((e) => {
                                var multiplierForBuyAndSell =
                                    tradeCardData?.tradeCardOptionsDataList?.[0].recommendationType == "BUY" ? -1 : 1;
                                var entryPriceWithoutLot =
                                    tradeCardData?.tradeCardOptionsDataList?.[0].suggestedPrice *
                                    multiplierForBuyAndSell *
                                    (tradeCardData?.tradeCardOptionsDataList?.[0].ratio ?? 1);
                                var stopLoss = (
                                    tradeCardData.stopLoss != null && tradeCardData.stopLoss != 0.0
                                        ? (tradeCardData.tradeCardOptionsDataList ?? []).length == 1
                                            ? (entryPriceWithoutLot -
                                                  (tradeCardData.stopLoss ?? 0) / (tradeCardData.lotSize ?? 1)) *
                                              multiplierForBuyAndSell
                                            : tradeCardData.stopLoss < 0
                                            ? tradeCardData.stopLoss * -1
                                            : tradeCardData.stopLoss
                                        : 0
                                )?.toFixed(2);
                                var targetPrice = (
                                    tradeCardData.targetPrice != null && tradeCardData.targetPrice != 0.0
                                        ? (tradeCardData.tradeCardOptionsDataList ?? []).length == 1
                                            ? (entryPriceWithoutLot -
                                                  (tradeCardData.targetPrice ?? 0) / (tradeCardData.lotSize ?? 1)) *
                                              multiplierForBuyAndSell
                                            : tradeCardData.targetPrice
                                        : 0
                                )?.toFixed(2);

                                dispatch(
                                    updateMultiField({
                                        order_type: ORDER_TYPES[1],
                                        stopLoss: `${stopLoss}`,
                                        targetPrice: `${targetPrice}`,
                                        shouldPlaceGtt: true,
                                        isNfo: isFuturesOrOptions(tradeCardData.term),
                                        source: source
                                    })
                                );
                            });
                        Mixpanel.track("cta_clicked", {
                            page: source,
                            widget: source,
                            cta_text: type,
                            tab: tab,
                            symbol: ele?.stockName,
                            expiry_date: ele?.expectedDurationEnum,
                            is_tradeidea: true,
                            target_hit_miss: hitOrMiss ?? null,
                            instrument_name: getInstrument(ele?.stockName),
                            is_watchlisted: ele?.watchListIds ? true : false
                        });
                    } else {
                        dispatch(
                            setOpenPlaceOrderBasketSlice({
                                isBasket: true,
                                isOpen: true,
                                finCode: tradeCardData.finCode,
                                tradeCardData,
                                type: "BUY",
                                source: source,
                                exchange,
                                basketDetails: tradeCardOptionsDataList?.map((item) => ({
                                    symbol: item.symbol,
                                    exchange,
                                    instrument: "OPTSTK",
                                    segment: "D",
                                    quantity: item?.ratio * item?.lotSize,
                                    suggestedPrice: item.suggestedPrice,
                                    price: item.nseLtpPrice,
                                    closePrice: item.suggestedPrice,
                                    security_id: item.exchangeToken,
                                    txn_type: item.recommendationType,
                                    ratio: item.ratio,
                                    trigger_price: ""
                                }))
                            })
                        );
                        Mixpanel.track("cta_clicked", {
                            page: source,
                            widget: source,
                            cta_text: type,
                            tab: tab,
                            symbol: ele?.stockName,
                            expiry_date: ele?.expectedDurationEnum,
                            is_tradeidea: true,
                            target_hit_miss: hitOrMiss ?? null,
                            instrument_name: getInstrument(ele?.stockName),
                            is_watchlisted: ele?.watchListIds ? true : false
                        });
                    }
                } else {
                    dispatch(
                        setOpenPlaceOrderSlice({
                            isOpen: true,
                            type,
                            finCode,
                            tradeCardData,
                            exchange,
                            source: source
                        })
                    )
                        .unwrap()
                        .then((e) => {
                            dispatch(
                                updateMultiField({
                                    productType: (isFuturesOrOptions(tradeCardData.term)
                                        ? PRODUCT_TYPES_FUTURES
                                        : PRODUCT_TYPES_STOCKS)[
                                        type === "SELL" && !isFuturesOrOptions(tradeCardData.term) ? 1 : 0
                                    ].code,
                                    order_type: ORDER_TYPES[1],
                                    stopLoss: `${tradeCardData.stopLoss ?? "0"}`,
                                    targetPrice: `${tradeCardData.targetPrice ?? "0"}`,
                                    shouldPlaceGtt: true,
                                    isNfo: isFuturesOrOptions(tradeCardData.term),
                                    source: source
                                })
                            );
                        });
                    Mixpanel.track("cta_clicked", {
                        page: source,
                        widget: source,
                        cta_text: type,
                        tab: tab,
                        symbol: ele?.stockName,
                        is_tradeidea: true,
                        target_hit_miss: hitOrMiss ?? null,
                        instrument_name: getInstrument(ele?.stockName),
                        is_watchlisted: ele?.watchListIds ? true : false
                    });
                }
            }}
        >
            Place {isMiniTradeCard && type?.toLowerCase()} order
            <FaArrowRight size={14} className="" color={isMiniTradeCard ? "#0862BC" : "#FFF"} />
        </Button>
    );
}

export function TradeCardExitBtn({
    disabled = false,
    type: tempType,
    finCode,
    tradeCardData,
    source,
    tab,
    ele,
    hitOrMiss,
    isClosed,
    classNames = "",
    onClick = () => {}
}) {
    const type = tempType === "BUY" || tempType === "OPTIONS" ? "BUY" : "SELL";

    const dispatch = useDispatch();

    const exchange =
        ["SENSEX", "BANKEX"]?.filter((ele) =>
            `${tradeCardData?.nseSymbol}-${tradeCardData?.bseSymbol}-${tradeCardData?.compName}-${tradeCardData?.stockName}-${tradeCardData?.name}`?.includes(
                ele
            )
        )?.length > 0
            ? "BSE"
            : "NSE";

    return (
        <button
            disabled={disabled}
            onClick={(e) => {
                onClick();
                e.preventDefault();
                e.stopPropagation();
                dispatch(resetPlaceOrder());
                if (isOptions(tradeCardData.term)) {
                    const tradeCardOptionsDataList = tradeCardData.tradeCardOptionsDataList;
                    if (tradeCardOptionsDataList.length === 1) {
                        dispatch(
                            setOpenPlaceOrderSlice({
                                exchange,
                                isOpen: true,
                                type: tradeCardOptionsDataList[0].recommendationType === "BUY" ? "SELL" : "BUY",
                                finCode: tradeCardOptionsDataList[0].finCode,
                                tradeCardData,
                                source: source
                            })
                        )
                            .unwrap()
                            .then((e) => {
                                dispatch(
                                    updateMultiField({
                                        order_type: ORDER_TYPES[1],
                                        shouldPlaceGtt: false,
                                        isNfo: isFuturesOrOptions(tradeCardData.term)
                                    })
                                );
                            });
                    } else {
                        dispatch(
                            setOpenPlaceOrderBasketSlice({
                                isBasket: true,
                                isOpen: true,
                                finCode: tradeCardData.finCode,
                                tradeCardData,
                                type: "SELL",
                                source: source,
                                exchange,
                                basketDetails: tradeCardOptionsDataList?.map((item) => ({
                                    symbol: item.symbol,
                                    exchange,
                                    instrument: "OPTSTK",
                                    segment: "D",
                                    quantity: item?.ratio * item?.lotSize,
                                    suggestedPrice: item.suggestedPrice,
                                    price: item.nseLtpPrice,
                                    closePrice: item.closurePrice,
                                    security_id: item.exchangeToken,
                                    txn_type: item.recommendationType === "BUY" ? "SELL" : "BUY",
                                    ratio: item.ratio,
                                    trigger_price: ""
                                }))
                            })
                        );
                    }
                } else {
                    dispatch(
                        setOpenPlaceOrderSlice({
                            isOpen: true,
                            type: type === "BUY" ? "SELL" : "BUY",
                            finCode,
                            tradeCardData,
                            source: source,
                            exchange
                        })
                    )
                        .unwrap()
                        .then((e) => {
                            dispatch(
                                updateMultiField({
                                    order_type: ORDER_TYPES[1],
                                    shouldPlaceGtt: false,
                                    isNfo: isFuturesOrOptions(tradeCardData.term)
                                })
                            );
                        });
                    Mixpanel.track("cta_clicked", {
                        page: source,
                        widget: source,
                        cta_text: "exit_trade",
                        tab: tab,
                        symbol: ele?.stockName,
                        is_tradeidea: true,
                        target_hit_miss: hitOrMiss ?? null,
                        instrument_name: getInstrument(ele?.stockName),
                        is_watchlisted: ele?.watchListIds ? true : false
                    });
                }
            }}
            className={cn(
                "cursor-pointer flex justify-center items-center gap-2 self-stretch px-0 py-1.5 rounded-lg",
                isClosed
                    ? "bg-primary/90 text-white disabled:bg-slate-300  disabled:border-slate-300"
                    : "text-primary/90 bg-[#FFF] disabled:bg-slate-300  disabled:border-slate-300",
                classNames
            )}
        >
            <p className="text-base font-bold leading-7">Exit trade</p>
            <FaArrowRight size={16} className="" color={isClosed ? "#FFFFFF" : "#00439D"} />
        </button>
    );
}

export function BuySellBtnStockDetails({ type = "", finCode, exchange, className }) {
    const dispatch = useDispatch();

    return (
        <Button
            variant="outline"
            className={cn(
                "p-0 border-none w-12 lg:w-[118px] h-7 lg:h-10 text-xs lg:text-base font-bold text-white hover:text-white",
                {
                    "bg-[#26A649] hover:bg-[#26A649BF]": type === "BUY",
                    "bg-[#EB4E2C] hover:bg-[#EB4E2CBF]": type === "SELL"
                },
                className
            )}
            onClick={() => {
                dispatch(resetPlaceOrder());
                dispatch(
                    setOpenPlaceOrderSlice({
                        isOpen: true,
                        type,
                        finCode,
                        exchange,
                        source: "stock_page"
                    })
                );
            }}
        >
            {capitalized(type)}
        </Button>
    );
}
